import { useEffect, useState } from "react";
import {
  refreshEmailVerification,
  sendEmailVerification,
  signOut,
  verificationCooldownRemaining,
} from "./session";

const INK = "#eef1f6";
const BG = "#0f1115";
const SURFACE = "#1b2029";
const LINE = "#39414f";
const ACCENT = "#4c8dff";

const wrap = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: BG,
  color: INK,
  padding: 24,
};

const card = {
  width: "100%",
  maxWidth: 380,
  display: "flex",
  flexDirection: "column",
  gap: 12,
  padding: 28,
  borderRadius: 8,
  background: SURFACE,
  border: `1px solid ${LINE}`,
  color: INK,
};

const button = (kind, disabled) => ({
  minHeight: 42,
  padding: "10px 12px",
  borderRadius: 8,
  border: kind === "primary" ? "none" : `1px solid ${LINE}`,
  background: kind === "primary" ? ACCENT : "#0f131a",
  color: INK,
  fontWeight: 600,
  cursor: disabled ? "wait" : "pointer",
  opacity: disabled ? 0.65 : 1,
});

export default function EmailVerificationGate({ onVerified, onSignOut }) {
  const initialRemaining = verificationCooldownRemaining();
  const [busy, setBusy] = useState("");
  const [remaining, setRemaining] = useState(initialRemaining);
  const [message, setMessage] = useState(
    initialRemaining > 0 ? "인증 메일을 보냈습니다. 메일함을 확인해 주세요." : "",
  );
  const [error, setError] = useState("");

  useEffect(() => {
    const timer = window.setInterval(() => {
      setRemaining(verificationCooldownRemaining());
    }, 500);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (remaining > 0 && !message && !error) {
      setMessage("인증 메일을 보냈습니다. 메일함을 확인해 주세요.");
    }
  }, [error, message, remaining]);

  const send = async () => {
    if (busy || remaining > 0) return;
    setBusy("send");
    setError("");
    try {
      await sendEmailVerification();
      setRemaining(verificationCooldownRemaining());
      setMessage("인증 메일을 보냈습니다. 메일함을 확인해 주세요.");
    } catch (err) {
      setMessage("");
      setRemaining(verificationCooldownRemaining());
      setError(err?.message || "인증 메일을 보내지 못했습니다. 잠시 후 다시 시도하세요.");
    } finally {
      setBusy("");
    }
  };

  const recheck = async () => {
    if (busy) return;
    setBusy("check");
    setError("");
    try {
      const state = await refreshEmailVerification();
      if (!state?.emailVerified || state.requiresVerification) {
        setError("아직 이메일 확인이 완료되지 않았습니다.");
        return;
      }
      setMessage("이메일 확인이 완료되었습니다.");
      await onVerified?.();
    } catch (err) {
      setError(err?.message || "이메일 확인 상태를 불러오지 못했습니다. 잠시 후 다시 시도하세요.");
    } finally {
      setBusy("");
    }
  };

  const logout = () => {
    if (busy) return;
    signOut();
    onSignOut?.();
  };

  const seconds = Math.ceil(remaining / 1000);
  return (
    <div style={wrap} data-testid="email-verification-gate">
      <section style={card} aria-labelledby="email-verification-title">
        <h1 id="email-verification-title" style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>
          이메일 확인 필요
        </h1>
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, color: "#c8d0dc" }}>
          가입한 이메일로 보낸 확인 링크를 연 뒤 이 화면으로 돌아와 재검사해 주세요.
        </p>
        <div aria-live="polite" style={{ minHeight: 20 }}>
          {message && <p style={{ margin: 0, fontSize: 13, color: "#8fc3ff" }}>{message}</p>}
          {error && <p role="alert" style={{ margin: 0, fontSize: 13, color: "#f28b82" }}>{error}</p>}
        </div>
        <button
          type="button"
          disabled={Boolean(busy) || remaining > 0}
          style={button("secondary", Boolean(busy) || remaining > 0)}
          onClick={send}
        >
          {busy === "send"
            ? "보내는 중..."
            : remaining > 0
            ? `재전송 (${seconds}초)`
            : "인증 메일 보내기"}
        </button>
        <button
          type="button"
          disabled={Boolean(busy)}
          style={button("primary", Boolean(busy))}
          onClick={recheck}
        >
          {busy === "check" ? "확인 중..." : "확인 완료 재검사"}
        </button>
        <button
          type="button"
          disabled={Boolean(busy)}
          style={button("secondary", Boolean(busy))}
          onClick={logout}
        >
          로그아웃
        </button>
      </section>
    </div>
  );
}
